import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  Renderer2
} from "@angular/core";

import { BehaviorSubject, Subscription } from "rxjs";

import { SelectDropdownOptionComponent } from "../shared/select-dropdown-option.component";

import { SelectClasses } from "./base-select-classes";
import { BaseSelectComponent } from "./base-select.component";

@Directive()
export abstract class OptionsListComponent<Model>
  implements OnInit, AfterViewInit, OnDestroy {
  abstract header: ElementRef;
  abstract queryInput: ElementRef;
  abstract scrollContainer: ElementRef;

  abstract optionComponents: QueryList<any>;

  get selectClasses(): SelectClasses {
    return this.parent.selectClasses;
  }

  get queryString(): string {
    if (this.queryInput == null) {
      return "";
    }
    return (this.queryInput.nativeElement as HTMLInputElement).value.trim();
  }

  get options(): Model[] {
    return this._options;
  }
  set options(options: Model[]) {
    this._options = options;
    this.optionsChanged.emit(options);
  }
  private _options: Model[];
  optionsChanged: EventEmitter<Model[]> = new EventEmitter<Model[]>();

  focusedOption: BehaviorSubject<Model | undefined> = new BehaviorSubject<
    Model | undefined
  >(undefined);

  @Input()
  disabled = false;

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      this.focusMouse = false;
      this.scrollFocusedOption(1);
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      this.focusMouse = false;
      this.scrollFocusedOption(-1);
    }
  }

  // needed to avoid conflicts btwn focus from mouse & from keybindings
  @HostListener("mousemove", ["$event"])
  onMouseMove(): void {
    this.focusMouse = true;
  }

  @HostListener("wheel", ["$event"])
  @HostListener("mousewheel", ["$event"])
  onWheel(event: WheelEvent): void {
    if (this.scrollContainer == null) {
      return;
    }
    const scrollEl: HTMLElement = this.scrollContainer.nativeElement;
    if (scrollEl.scrollTop + event.deltaY < 0) {
      if (scrollEl.scrollTop !== 0) {
        scrollEl.scrollTop = 0;
      }
      event.preventDefault();
      return;
    }

    const maxScrollTop: number = scrollEl.scrollHeight - scrollEl.offsetHeight;
    if (scrollEl.scrollTop + event.deltaY > maxScrollTop) {
      if (scrollEl.scrollTop !== maxScrollTop) {
        scrollEl.scrollTop = maxScrollTop;
      }
      event.preventDefault();
    }
  }

  focusMouse: boolean = false;

  public parent: BaseSelectComponent<Model, any, any>;

  constructor(
    parent: any,
    protected renderer: Renderer2,
    public elementRef: ElementRef
  ) {
    this.parent = parent;
  }

  protected initializeFocusedOption: boolean = true;

  ngOnInit(): void {
    this.initOptionsSubscription();
  }

  ngAfterViewInit(): void {
    this.initQueryInputSubscription();
    this.updateScrollPos();
  }

  ngOnDestroy(): void {
    this.optionsSubscription.unsubscribe();
    if (this.queryInputSubscription) {
      this.queryInputSubscription.unsubscribe();
    }

    if (this.queryInput) {
      // blur input el to prevent node leak - apparently chrome retains the el if it's focused on destroy (¿¿¿???)
      // TODO: see if chrome is still bugging here
      (this.queryInput.nativeElement as HTMLElement).blur();
    }
  }

  protected abstract setHostClasses(): void;

  protected optionsSubscription: Subscription;
  protected abstract initOptionsSubscription(): void;

  protected queryInputSubscription: Subscription;
  protected abstract initQueryInputSubscription(): void;

  protected focusQueryInput(): any {
    if (this.queryInput != null) {
      (this.queryInput.nativeElement as HTMLElement).focus();
    }
  }

  protected getFocusedOptionComponent():
    | SelectDropdownOptionComponent<Model>
    | undefined {
    if (this.optionComponents == null) {
      return;
    }
    return this.optionComponents
      .toArray()
      .find((component) => component.option === this.focusedOption.value);
  }

  protected getFocusedIdx(): number {
    if (this.optionComponents == null) {
      return -1;
    }
    const optionComponents = this.optionComponents.toArray();
    if (!optionComponents.length) {
      return -1;
    }
    return optionComponents.findIndex(
      (component) => component.option === this.focusedOption.value
    );
  }

  onOptionsContainerScroll(event: Event): void {
    let el = event.target as HTMLElement;
    if (el.scrollTop + el.clientHeight > el.scrollHeight - 10) {
      this.parent.getNextOptionsPage();
    }
  }

  protected setInitialFocusedOption(): void {
    if (this.parent.value != null) {
      const focus: Model =
        this.options.find((option) => this.parent.isSelected(option)) ||
        this.options[0];
      this.focusedOption.next(focus);
      return;
    }
    this.focusedOption.next(this.options[0]);
  }

  scrollFocusedOption(steps: number = 1): void {
    if (this.focusedOption.value == null) {
      return;
    }

    let focusIndex = this.options.indexOf(this.focusedOption.value);
    if (steps < 0 && focusIndex + steps >= 0) {
      this.updateFocusedOption(this.options[focusIndex + steps], true);
      return;
    }

    if (steps > 0 && focusIndex + steps < this.options.length) {
      this.updateFocusedOption(this.options[focusIndex + steps], true);
      return;
    }
  }

  updateFocusedOption(option?: Model, forceScroll: boolean = false): void {
    this.focusedOption.next(option);

    if (forceScroll) {
      this.updateScrollPos();
    }
  }

  protected updateScrollPos(centered: boolean = false): void {
    let focusedOption:
      | SelectDropdownOptionComponent<Model>
      | undefined = this.getFocusedOptionComponent();
    if (!this.options || !this.options.length || focusedOption == null) {
      return;
    }
    let scrollContainer: HTMLElement = this.scrollContainer.nativeElement,
      focusedEl: HTMLElement = focusedOption.elemRef.nativeElement,
      scrollContainerPos = scrollContainer.getBoundingClientRect(),
      focusedElPos = focusedEl.getBoundingClientRect();

    if (centered) {
      this.renderer.setProperty(
        scrollContainer,
        "scrollTop",
        scrollContainer.scrollTop -
          (scrollContainerPos.top +
            scrollContainerPos.height / 2 -
            (focusedElPos.top + focusedElPos.height / 2))
      );
      return;
    }

    if (scrollContainerPos.top > focusedElPos.top) {
      this.renderer.setProperty(
        scrollContainer,
        "scrollTop",
        scrollContainer.scrollTop - (scrollContainerPos.top - focusedElPos.top)
      );
      return;
    }

    if (scrollContainerPos.bottom < focusedElPos.bottom) {
      this.renderer.setProperty(
        scrollContainer,
        "scrollTop",
        scrollContainer.scrollTop +
          (focusedElPos.bottom - scrollContainerPos.bottom)
      );
      return;
    }
  }
}
